import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { AuthService, UserService } from "../services/firebaseServices";

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const signingUp = useRef(false);

  useEffect(() => {
    const unsubscribe = AuthService.onAuthChange(async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        if (!signingUp.current) {
          try {
            const userProfile = await UserService.getProfile(firebaseUser.uid);
            if (userProfile) {
              setProfile(userProfile);
              setRole(userProfile.role || "customer");
            }
          } catch (err) {
            console.warn("Profile fetch error:", err.message);
          }
        }
      } else {
        setUser(null);
        setProfile(null);
        setRole(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const refreshProfile = async () => {
    if (!user) return;
    const updated = await UserService.getProfile(user.uid);
    setProfile(updated);
    setRole(updated?.role || "customer");
  };

  const signUp = async (email, password, displayName, phone) => {
    signingUp.current = true;
    try {
      const newUser = await AuthService.signUp(email, password, displayName, phone);
      await UserService.createProfile(newUser.uid, { email, displayName, phone });
      const freshProfile = await UserService.getProfile(newUser.uid);
      setProfile(freshProfile);
      setRole(freshProfile?.role || "customer");
      return newUser;
    } finally {
      signingUp.current = false;
    }
  };

  const signIn = async (email, password) => {
    const loggedIn = await AuthService.signIn(email, password);
    const freshProfile = await UserService.getProfile(loggedIn.uid);
    setProfile(freshProfile);
    setRole(freshProfile?.role || "customer");
    return loggedIn;
  };

  const signInWithGoogle = async () => {
    signingUp.current = true;
    try {
      const googleUser = await AuthService.signInWithGoogle();
      const existing = await UserService.getProfile(googleUser.uid);
      if (!existing) {
        await UserService.createProfile(googleUser.uid, {
          email: googleUser.email,
          displayName: googleUser.displayName,
          phone: googleUser.phoneNumber || "",
        });
      }
      const freshProfile = await UserService.getProfile(googleUser.uid);
      setProfile(freshProfile);
      setRole(freshProfile?.role || "customer");
      return googleUser;
    } finally {
      signingUp.current = false;
    }
  };

  const signOut = async () => { await AuthService.signOut(); };
  const resetPassword = async (email) => { await AuthService.resetPassword(email); };

  const value = {
    user, profile, role, loading,
    isAdmin: role === "admin",
    isStaff: role === "staff" || role === "admin",
    isAuthenticated: !!user,
    signUp, signIn, signInWithGoogle, signOut, resetPassword, refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
